/**
 * useLanguageChooser Hook
 * Business logic for language selection
 *
 * DDD Layer: Application
 * Responsibilities:
 * - Manage language state
 * - Handle language switching
 * - Provide language options
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { FlagEnIcon, FlagUaIcon } from '../Icons';
import { LanguageOption } from './LanguageSelect';
import Keys from '../../../common/constants/Keys';

export const useLanguageChooser = () => {
  const { i18n } = useTranslation();

  const languageOptions: LanguageOption[] = useMemo(
    () => [
      {
        value: 'en',
        label: 'English',
        icon: <FlagEnIcon width={20} height={15} />,
      },
      {
        value: 'uk',
        label: 'Українська',
        icon: <FlagUaIcon width={20} height={15} />,
      },
    ],
    []
  );

  const currentLanguage = useMemo(() => {
    const lang = (i18n.language || '').split('-')[0];
    return languageOptions.find(option => option.value === lang) || languageOptions[0];
  }, [languageOptions, i18n.language]);

  const handleLanguageChange = (selectedOption: SingleValue<LanguageOption>) => {
    if (selectedOption && selectedOption.value !== i18n.language) {
      i18n.changeLanguage(selectedOption.value);
      localStorage.setItem(Keys.LANGUAGE, selectedOption.value);
    }
  };

  return {
    languageOptions,
    currentLanguage,
    handleLanguageChange,
  };
};
